import { DayMonth, ClockET, EasternISODate } from '../shared/format';
import { DateOrNull, NumOrNull, Str, StrOrNull, type OEPRow } from '../shared/rows';

/**
 * Activity (AGU staff) — who in the active season has signed in, opened OEP and done their part, by role,
 * with per-day activity and CSV exports. Pure: every string and number the view shows is built here.
 * Definitions of a sign-in and an OEP visit live with the SQL (metadata/queries/oep/participant-activity.sql).
 */

/** Pinned MJ Query IDs (metadata/queries/oep-*-activity.json); MJ: Query Permissions grant both to OEP Staff only. */
export const OEP_PARTICIPANT_ACTIVITY_QUERY_ID = '0E900000-0000-4000-9000-000000000056';
export const OEP_DAILY_ACTIVITY_QUERY_ID = '0E900000-0000-4000-9000-000000000058';

export type OEPActivityRole = 'Presenter' | 'Reviewer' | 'Nominator' | 'Committee';
/** Season order (presenters upload, reviewers review, nominators nominate, committees pick winners). */
export const OEP_ACTIVITY_ROLES: readonly OEPActivityRole[] = ['Presenter', 'Reviewer', 'Nominator', 'Committee'];
const ROLE_LABEL: Readonly<Record<OEPActivityRole, string>> = {
    Presenter: 'Presenters', Reviewer: 'Reviewers', Nominator: 'Nominators', Committee: 'Section committees',
};

/** One OEP Participant Activity row: a person in one role. */
export interface OEPActivityPerson {
    Role: OEPActivityRole; UserID: string; Name: string; Email: string;
    Institution: string | null; CareerStage: string | null; Section: string | null;
    PresentationCode: string | null; PresentationTitle: string | null; PresentationFormat: string | null;
    /** Confirmed draft uploads, including ones later replaced. */
    DraftUploads: number;
    /** The current draft (Status Uploaded); null when there is none, even if earlier ones were replaced. */
    DraftFileName: string | null; DraftFilePath: string | null; DraftUploadedAt: Date | null;
    FinalUploads: number; FinalFileName: string | null; FinalFilePath: string | null; FinalUploadedAt: Date | null;
    ReviewsAssigned: number | null; ReviewsSubmitted: number | null; NominationsSubmitted: number | null;
    SlateStatus: 'Draft' | 'Submitted' | null;
    FirstSignInAt: Date | null; LastSignInAt: Date | null; SignInDays: number;
    FirstOEPAt: Date | null; LastOEPAt: Date | null; OEPVisits: number; OEPPageViews: number; OEPDays: number;
    Canceled?: boolean;
}

/** One OEP Daily Activity row: a role on one US Eastern date. */
export interface OEPActivityDay {
    Role: OEPActivityRole; Day: string;
    SignedIn: number; InOEP: number; NewInOEP: number; OEPVisits: number; OEPPageViews: number; DraftUploads: number; FinalUploads: number;
}

const n = (r: OEPRow, f: string): number => NumOrNull(r, f) || 0;

function parseRole(r: OEPRow): OEPActivityRole {
    const role = Str(r, 'Role');
    const known = OEP_ACTIVITY_ROLES.find((k) => k === role);
    if (!known) throw new Error(`Unexpected OEP activity role "${role}".`);
    return known;
}

/** "First Last", skipping blank and "." placeholder parts (roster rows with no surname); the email when no name is left. */
function displayName(r: OEPRow): string {
    const parts = [StrOrNull(r, 'FirstName'), StrOrNull(r, 'LastName')].map((p) => (p || '').trim()).filter((p) => p !== '' && p !== '.');
    return parts.length > 0 ? parts.join(' ') : Str(r, 'Email');
}

/** One OEP Participant Activity row → a person in one role; refuses a role outside OEP_ACTIVITY_ROLES. */
export function ParsePersonRow(r: OEPRow): OEPActivityPerson {
    const slate = StrOrNull(r, 'SlateStatus');
    return {
        Role: parseRole(r), UserID: Str(r, 'UserID'), Name: displayName(r), Email: Str(r, 'Email'),
        Institution: StrOrNull(r, 'Institution'), CareerStage: StrOrNull(r, 'CareerStage'), Section: StrOrNull(r, 'SectionName'),
        PresentationCode: StrOrNull(r, 'PresentationCode'), PresentationTitle: StrOrNull(r, 'PresentationTitle'), PresentationFormat: StrOrNull(r, 'PresentationFormat'),
        DraftUploads: n(r, 'DraftUploads'), DraftFileName: StrOrNull(r, 'DraftFileName'), DraftFilePath: StrOrNull(r, 'DraftFilePath'), DraftUploadedAt: DateOrNull(r, 'DraftUploadedAt'),
        FinalUploads: n(r, 'FinalUploads'), FinalFileName: StrOrNull(r, 'FinalFileName'), FinalFilePath: StrOrNull(r, 'FinalFilePath'), FinalUploadedAt: DateOrNull(r, 'FinalUploadedAt'),
        ReviewsAssigned: NumOrNull(r, 'ReviewsAssigned'), ReviewsSubmitted: NumOrNull(r, 'ReviewsSubmitted'), NominationsSubmitted: NumOrNull(r, 'NominationsSubmitted'),
        SlateStatus: slate === 'Draft' || slate === 'Submitted' ? slate : null,
        FirstSignInAt: DateOrNull(r, 'FirstSignInAt'), LastSignInAt: DateOrNull(r, 'LastSignInAt'), SignInDays: n(r, 'SignInDays'),
        FirstOEPAt: DateOrNull(r, 'FirstOEPAt'), LastOEPAt: DateOrNull(r, 'LastOEPAt'), OEPVisits: n(r, 'OEPVisits'), OEPPageViews: n(r, 'OEPPageViews'), OEPDays: n(r, 'OEPDays'),
    };
}

/** The SQL DATE arrives as 'YYYY-MM-DD' or a midnight-UTC timestamp, depending on the driver; either way its first 10 characters are the date. */
export function ParseDayRow(r: OEPRow): OEPActivityDay {
    return {
        Role: parseRole(r), Day: Str(r, 'Day').slice(0, 10),
        SignedIn: n(r, 'SignedIn'), InOEP: n(r, 'InOEP'), NewInOEP: n(r, 'NewInOEP'), OEPVisits: n(r, 'OEPVisits'), OEPPageViews: n(r, 'OEPPageViews'),
        DraftUploads: n(r, 'DraftUploads'), FinalUploads: n(r, 'FinalUploads'),
    };
}

/** mj-tab-nav TabConfig shape (key/label/badge). */
export interface OEPActivityTab { key: OEPActivityRole; label: string; badge: number }

/** One tab per OEP role, in season order, badged with the role's people count (roles with nobody still show). */
export function RoleTabs(people: readonly OEPActivityPerson[]): OEPActivityTab[] {
    return OEP_ACTIVITY_ROLES.map((role) => ({ key: role, label: ROLE_LABEL[role], badge: people.filter((p) => p.Role === role).length }));
}

/** The first role, in season order, that has anyone in it; Presenter when nobody does. */
export function DefaultRole(people: readonly OEPActivityPerson[]): OEPActivityRole {
    return OEP_ACTIVITY_ROLES.find((role) => people.some((p) => p.Role === role)) || 'Presenter';
}

export interface OEPActivityTile { Label: string; Value: string; Note: string }

const frac = (x: number, y: number): string => `${x} / ${y}`;
const hasDraft = (p: OEPActivityPerson): boolean => p.DraftFileName !== null;
const sum = (xs: readonly number[]): number => xs.reduce((a, b) => a + b, 0);

/** Signed in / opened OEP out of everyone in the role, the role's own progress, and people in OEP today (ET). */
export function ActivityTiles(role: OEPActivityRole, people: readonly OEPActivityPerson[], days: readonly OEPActivityDay[], now: Date): OEPActivityTile[] {
    const mine = people.filter((p) => p.Role === role);
    const total = mine.length;
    const today = EasternISODate(now);
    const inToday = days.find((d) => d.Role === role && d.Day === today)?.InOEP || 0;
    return [
        { Label: 'Signed in', Value: frac(mine.filter((p) => p.LastSignInAt !== null).length, total), Note: 'have signed in at least once' },
        { Label: 'Opened OEP', Value: frac(mine.filter((p) => p.LastOEPAt !== null).length, total), Note: 'have opened the platform' },
        progressTile(role, mine),
        { Label: 'Today', Value: String(inToday), Note: 'in OEP today (ET)' },
    ];
}

function progressTile(role: OEPActivityRole, mine: readonly OEPActivityPerson[]): OEPActivityTile {
    switch (role) {
        case 'Presenter':
            return { Label: 'Drafts', Value: frac(mine.filter(hasDraft).length, mine.length), Note: 'have a draft uploaded' };
        case 'Reviewer':
            return { Label: 'Reviews', Value: frac(sum(mine.map((p) => p.ReviewsSubmitted || 0)), sum(mine.map((p) => p.ReviewsAssigned || 0))), Note: 'feedback submitted' };
        case 'Nominator':
            return { Label: 'Nominations', Value: String(sum(mine.map((p) => p.NominationsSubmitted || 0))), Note: 'submitted' };
        case 'Committee': {
            const sections = new Set(mine.map((p) => p.Section || ''));
            const submitted = new Set(mine.filter((p) => p.SlateStatus === 'Submitted').map((p) => p.Section || ''));
            return { Label: 'Winners', Value: frac(submitted.size, sections.size), Note: 'sections submitted' };
        }
    }
}

export interface OEPActivityDayVM extends Omit<OEPActivityDay, 'Role'> { Label: string }

/** How many US Eastern days the daily table shows. */
export const OEP_ACTIVITY_DAYS = 14;
const weekdayDay = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' });

/** The last 14 US Eastern days ending today for one role, newest first; a day with no activity is all zeros. */
export function DailyRows(role: OEPActivityRole, days: readonly OEPActivityDay[], now: Date): OEPActivityDayVM[] {
    const [y, m, d] = EasternISODate(now).split('-').map(Number);
    const out: OEPActivityDayVM[] = [];
    for (let i = 0; i < OEP_ACTIVITY_DAYS; i++) {
        const date = new Date(Date.UTC(y, m - 1, d - i));
        const iso = date.toISOString().slice(0, 10);
        const found = days.find((x) => x.Role === role && x.Day === iso);
        out.push({
            Day: iso, Label: weekdayDay.format(date).replace(',', ''),
            SignedIn: found?.SignedIn || 0, InOEP: found?.InOEP || 0, NewInOEP: found?.NewInOEP || 0, OEPVisits: found?.OEPVisits || 0,
            OEPPageViews: found?.OEPPageViews || 0, DraftUploads: found?.DraftUploads || 0, FinalUploads: found?.FinalUploads || 0,
        });
    }
    return out;
}

export type OEPActivityShow = 'all' | 'signed-in' | 'never-signed-in' | 'uploaded' | 'not-uploaded' | 'inactive';

export interface OEPActivityShowOption { Value: OEPActivityShow; Label: string }

/** The People filter; the draft and inactive options only make sense for presenters. */
export function ShowOptions(role: OEPActivityRole): OEPActivityShowOption[] {
    const base: OEPActivityShowOption[] = [
        { Value: 'all', Label: 'Everyone' }, { Value: 'signed-in', Label: 'Signed in' }, { Value: 'never-signed-in', Label: 'Never signed in' },
    ];
    return role === 'Presenter'
        ? [...base, { Value: 'uploaded', Label: 'Draft uploaded' }, { Value: 'not-uploaded', Label: 'No draft yet' }, { Value: 'inactive', Label: 'Inactive' }]
        : base;
}

/** People passing the Show choice and whose name, email, section, presentation code or title contains the search (trimmed, any case). */
export function FilterPeople(people: readonly OEPActivityPerson[], show: OEPActivityShow, search: string): OEPActivityPerson[] {
    const q = search.trim().toLowerCase();
    return people.filter((p) => {
        const passes =
            show === 'all' ? true
                : show === 'signed-in' ? p.LastSignInAt !== null
                    : show === 'never-signed-in' ? p.LastSignInAt === null
                        : show === 'uploaded' ? hasDraft(p)
                            : show === 'inactive' ? p.Canceled === true
                                : !hasDraft(p);
        if (!passes) return false;
        if (q === '') return true;
        return [p.Name, p.Email, p.Section, p.PresentationCode, p.PresentationTitle].some((v) => (v || '').toLowerCase().includes(q));
    });
}

export type OEPActivityTone = 'ok' | 'warn' | 'neutral';
export interface OEPActivityPersonVM {
    UserID: string; Name: string; Email: string; Where: string; Title: string | null;
    Progress: string; ProgressNote: string; ProgressTone: OEPActivityTone;
    LastSeen: string; Visits: string;
}

/** "29 October, 10:47 a.m. ET". */
function whenET(d: Date): string {
    return `${DayMonth(d)}, ${ClockET(d)}`;
}

function later(a: Date | null, b: Date | null): Date | null {
    if (a === null) return b;
    if (b === null) return a;
    return a.getTime() >= b.getTime() ? a : b;
}

function progress(p: OEPActivityPerson): Pick<OEPActivityPersonVM, 'Progress' | 'ProgressNote' | 'ProgressTone'> {
    switch (p.Role) {
        case 'Presenter':
            if (p.Canceled) return { Progress: 'Inactive (canceled)', ProgressNote: '', ProgressTone: 'warn' };
            return p.DraftFileName !== null
                ? { Progress: p.DraftFileName, ProgressNote: p.DraftUploadedAt ? `uploaded ${whenET(p.DraftUploadedAt)}` : '', ProgressTone: 'ok' }
                : { Progress: 'No draft yet', ProgressNote: '', ProgressTone: 'neutral' };
        case 'Reviewer': {
            const assigned = p.ReviewsAssigned || 0;
            const submitted = p.ReviewsSubmitted || 0;
            if (assigned === 0) return { Progress: 'Not matched yet', ProgressNote: '', ProgressTone: 'neutral' };
            return { Progress: `${submitted} / ${assigned} reviews submitted`, ProgressNote: '', ProgressTone: submitted >= assigned ? 'ok' : 'warn' };
        }
        case 'Nominator': {
            const count = p.NominationsSubmitted || 0;
            return { Progress: `${count} ${count === 1 ? 'nomination' : 'nominations'} submitted`, ProgressNote: '', ProgressTone: count > 0 ? 'ok' : 'neutral' };
        }
        case 'Committee':
            return p.SlateStatus === 'Submitted' ? { Progress: 'Winners submitted', ProgressNote: '', ProgressTone: 'ok' }
                : p.SlateStatus === 'Draft' ? { Progress: 'Winners in progress', ProgressNote: '', ProgressTone: 'warn' }
                    : { Progress: 'Winners not started', ProgressNote: '', ProgressTone: 'neutral' };
    }
}

/** Table rows for people: progress for their role, last seen (the later of last sign-in and last OEP activity, ET), visits. */
export function PersonRows(people: readonly OEPActivityPerson[]): OEPActivityPersonVM[] {
    return people.map((p) => {
        const seen = later(p.LastSignInAt, p.LastOEPAt);
        return {
            UserID: p.UserID, Name: p.Name, Email: p.Email,
            Where: [p.PresentationCode, p.Section].filter((v): v is string => v !== null && v !== '').join(' · '),
            Title: p.PresentationTitle,
            ...progress(p),
            LastSeen: seen ? whenET(seen) : 'Never signed in',
            Visits: String(p.OEPVisits),
        };
    });
}

const stamp = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });

/** "2026-10-29 10:35" on the 24-hour US Eastern clock, for spreadsheets; '' for none. */
export function StampET(d: Date | null): string {
    if (d === null) return '';
    const parts = stamp.formatToParts(d);
    const get = (t: Intl.DateTimeFormatPartTypes): string => parts.find((x) => x.type === t)?.value || '';
    return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}`;
}

export type OEPCsvCell = string | number;
export type OEPCsvRow = Record<string, OEPCsvCell>;

const IDENTITY_COLUMNS = ['Name', 'Email', 'Institution', 'Career stage'] as const;
const ACTIVITY_COLUMNS = [
    'First sign-in (ET)', 'Last sign-in (ET)', 'Days signed in', 'First OEP visit (ET)', 'Last OEP activity (ET)', 'OEP visits', 'OEP page views', 'Days in OEP',
] as const;
const ROLE_COLUMNS: Readonly<Record<OEPActivityRole, readonly string[]>> = {
    Presenter: [
        'Section', 'Presentation code', 'Presentation title', 'Format',
        'Draft uploaded', 'Draft file', 'Draft file path', 'Draft uploaded at (ET)', 'Draft uploads',
        'Final uploaded', 'Final file', 'Final file path', 'Final uploaded at (ET)',
    ],
    Reviewer: ['Reviews assigned', 'Reviews submitted'],
    Nominator: ['Nominations submitted'],
    Committee: ['Section', 'Winners slate'],
};

/** People CSV columns for one role, in export order. */
export function PeopleCsvColumns(role: OEPActivityRole): string[] {
    return [...IDENTITY_COLUMNS, ...ROLE_COLUMNS[role], ...ACTIVITY_COLUMNS];
}

function roleCells(p: OEPActivityPerson): OEPCsvRow {
    switch (p.Role) {
        case 'Presenter':
            return {
                'Section': p.Section || '', 'Presentation code': p.PresentationCode || '', 'Presentation title': p.PresentationTitle || '', 'Format': p.PresentationFormat || '',
                'Draft uploaded': hasDraft(p) ? 'Yes' : 'No', 'Draft file': p.DraftFileName || '', 'Draft file path': p.DraftFilePath || '',
                'Draft uploaded at (ET)': StampET(p.DraftUploadedAt), 'Draft uploads': p.DraftUploads,
                'Final uploaded': p.FinalFileName !== null ? 'Yes' : 'No', 'Final file': p.FinalFileName || '', 'Final file path': p.FinalFilePath || '',
                'Final uploaded at (ET)': StampET(p.FinalUploadedAt),
            };
        case 'Reviewer':
            return { 'Reviews assigned': p.ReviewsAssigned || 0, 'Reviews submitted': p.ReviewsSubmitted || 0 };
        case 'Nominator':
            return { 'Nominations submitted': p.NominationsSubmitted || 0 };
        case 'Committee':
            return { 'Section': p.Section || '', 'Winners slate': p.SlateStatus || 'Not started' };
    }
}

/** People CSV rows (neutralized by the caller before ExportEngine.toCSV), keys in PeopleCsvColumns order. */
export function PeopleCsvRows(role: OEPActivityRole, people: readonly OEPActivityPerson[]): OEPCsvRow[] {
    return people.filter((p) => p.Role === role).map((p) => {
        const cells: OEPCsvRow = {
            'Name': p.Name, 'Email': p.Email, 'Institution': p.Institution || '', 'Career stage': p.CareerStage || '',
            ...roleCells(p),
            'First sign-in (ET)': StampET(p.FirstSignInAt), 'Last sign-in (ET)': StampET(p.LastSignInAt), 'Days signed in': p.SignInDays,
            'First OEP visit (ET)': StampET(p.FirstOEPAt), 'Last OEP activity (ET)': StampET(p.LastOEPAt),
            'OEP visits': p.OEPVisits, 'OEP page views': p.OEPPageViews, 'Days in OEP': p.OEPDays,
        };
        // ??, not ||: a count of 0 is a real value and must reach the CSV as 0, not as an empty cell.
        return Object.fromEntries(PeopleCsvColumns(role).map((c) => [c, cells[c] ?? ''])) as OEPCsvRow;
    });
}

/** Daily CSV columns; only presenters upload. */
export function DailyCsvColumns(role: OEPActivityRole): string[] {
    const base = ['Date (ET)', 'Signed in', 'In OEP', 'First time in OEP', 'OEP visits', 'OEP page views'];
    return role === 'Presenter' ? [...base, 'Drafts uploaded', 'Finals uploaded'] : base;
}

/** Daily CSV rows (neutralized by the caller), keys in DailyCsvColumns order. */
export function DailyCsvRows(role: OEPActivityRole, rows: readonly OEPActivityDayVM[]): OEPCsvRow[] {
    return rows.map((d) => {
        const cells: OEPCsvRow = {
            'Date (ET)': d.Day, 'Signed in': d.SignedIn, 'In OEP': d.InOEP, 'First time in OEP': d.NewInOEP, 'OEP visits': d.OEPVisits, 'OEP page views': d.OEPPageViews,
            'Drafts uploaded': d.DraftUploads, 'Finals uploaded': d.FinalUploads,
        };
        return Object.fromEntries(DailyCsvColumns(role).map((c) => [c, cells[c]])) as OEPCsvRow;
    });
}
